import { useEffect, useRef, useState } from "react";

const clamp = (v) => Math.min(6, Math.max(0.2, v));

// Wraps a broadcast graphic so it can be dragged (move), stretched (right / bottom edge) or scaled (corner).
// box = {x, y, sx, sy, nw} in 1920×1080 canvas px; k = on-screen scale of the canvas.
export function Movable({ id, box, editable, k = 1, onChange, className, style, children }) {
  const inner = useRef(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = inner.current; if (!el) return undefined;
    const ro = new ResizeObserver(() => setNat({ w: el.offsetWidth, h: el.offsetHeight }));
    ro.observe(el); return () => ro.disconnect();
  }, []);
  const drag = (e, kind) => {
    if (!editable || !onChange) return;
    e.preventDefault(); e.stopPropagation();
    const el = inner.current; const stage = el.closest("[data-stage]").getBoundingClientRect(); const r = el.getBoundingClientRect();
    const b0 = box || { x: Math.round((r.left - stage.left) / k), y: Math.round((r.top - stage.top) / k), sx: 1, sy: 1, nw: el.offsetWidth };
    const w = el.offsetWidth || 1; const h = el.offsetHeight || 1; const x0 = e.clientX; const y0 = e.clientY; let last = b0;
    const mv = (ev) => {
      const dx = (ev.clientX - x0) / k; const dy = (ev.clientY - y0) / k; const b = { ...b0 };
      if (kind === "move") { const bw = w * b0.sx; const bh = h * b0.sy; b.x = Math.round(Math.min(1920 - 80, Math.max(80 - bw, b0.x + dx))); b.y = Math.round(Math.min(1080 - 60, Math.max(60 - bh, b0.y + dy))); }
      if (kind === "r") b.sx = clamp(b0.sx + dx / w);
      if (kind === "b") b.sy = clamp(b0.sy + dy / h);
      if (kind === "c") { const f = clamp(b0.sx + dx / w) / b0.sx; b.sx = b0.sx * f; b.sy = b0.sy * f; }
      last = b; onChange(id, b, false);
    };
    const up = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); onChange(id, last, true); };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up);
  };
  const sx = box?.sx || 1; const sy = box?.sy || 1; const hs = Math.max(18, 14 / k);
  const handle = (kind, cls, cursor) => <span onPointerDown={(e) => drag(e, kind)} data-testid={`handle-${kind}-${id}`} className={`absolute rounded bg-[#FFC233] shadow ${cls}`} style={{ width: hs, height: hs, cursor, touchAction: "none" }} />;
  const content = (
    <div className="pointer-events-auto relative" style={box ? { width: nat.w * sx, height: nat.h * sy } : undefined}>
      <div ref={inner} style={box ? { width: box.nw, transform: `scale(${sx},${sy})`, transformOrigin: "0 0" } : undefined}>{children}</div>
      {editable && (
        <div onPointerDown={(e) => drag(e, "move")} className="absolute inset-0 cursor-move rounded outline-dashed outline-[3px] outline-[#FFC233]" style={{ touchAction: "none", background: "rgba(255,194,51,0.06)" }} data-testid={`movable-${id}`}>
          <span className="absolute left-0 top-0 -translate-y-full rounded-t bg-[#FFC233] px-2 font-bold text-black" style={{ fontSize: Math.max(14, 11 / k) }}>{id.replace("_", " ")} · {Math.round(sx * 100)}%{sx !== sy ? ` × ${Math.round(sy * 100)}%` : ""}</span>
          {handle("r", "right-0 top-1/2 -translate-y-1/2 translate-x-1/2", "ew-resize")}
          {handle("b", "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2", "ns-resize")}
          {handle("c", "bottom-0 right-0 translate-x-1/2 translate-y-1/2", "nwse-resize")}
        </div>)}
    </div>
  );
  return box
    ? <div className="pointer-events-none absolute left-0 top-0" style={{ ...style, transform: `translate(${box.x}px,${box.y}px)` }}>{content}</div>
    : <div className={`pointer-events-none ${className}`} style={style}>{content}</div>;
}
